// Pool-Kleidung Page - Digitaler Schrank
import React, { useState, useEffect } from 'react';
import { 
  Shirt, Plus, Search, User, History, Trash2, Edit, 
  CheckCircle, XCircle, ArrowRightLeft, Package, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { poolKleidungApi, usersApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const KleidungsTyp = {
  JACKE: 'JACKE',
  HOSE: 'HOSE',
  HANDSCHUH: 'HANDSCHUH'
};

const typLabels = {
  JACKE: 'Jacke',
  HOSE: 'Hose',
  HANDSCHUH: 'Handschuhe'
};

const typIcons = {
  JACKE: '/images/uniform/jacke.svg',
  HOSE: '/images/uniform/hose.svg',
  HANDSCHUH: '/images/uniform/handschuh.svg'
};

// Wiederverwendbarer Uniform-Icon-Renderer
const UniformIcon = ({ typ, size = 64, className = '' }) => (
  <img
    src={typIcons[typ]}
    alt={typ}
    width={size}
    height={size}
    className={`inline-block object-contain ${className}`}
    style={{ width: size, height: size }}
  />
);

export default function PoolKleidungPage() {
  const { user, hasEffectivePermission } = useAuthStore();
  const canManagePool = hasEffectivePermission('pool_kleidung.manage');
  const [activeTab, setActiveTab] = useState('JACKE');
  const [kleidung, setKleidung] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [statistik, setStatistik] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAusleihenDialog, setShowAusleihenDialog] = useState(false);
  const [showHistorieDialog, setShowHistorieDialog] = useState(false);
  const [selectedKleidung, setSelectedKleidung] = useState(null);
  const [historie, setHistorie] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [kleidungRes, mitarbeiterRes, statistikRes] = await Promise.all([
        poolKleidungApi.getAll(),
        usersApi.getAll(),
        poolKleidungApi.getStatistik()
      ]);
      setKleidung(kleidungRes.data || []);
      setMitarbeiter(mitarbeiterRes.data || []);
      setStatistik(statistikRes.data || {});
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAusleihen = (item) => {
    setSelectedKleidung(item);
    setShowAusleihenDialog(true);
  };

  const handleZurueckgeben = async (item) => {
    try {
      await poolKleidungApi.zurueckgeben(item.id, {});
      toast.success('Kleidung zurückgegeben');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zurückgeben');
    }
  };

  const handleShowHistorie = async (item) => {
    try {
      const response = await poolKleidungApi.getHistorie(item.id);
      setHistorie(response.data?.historie || []);
      setSelectedKleidung(item);
      setShowHistorieDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Historie');
    }
  };

  const handleDelete = async (item) => {
    if (!confirm(`Kleidungsstück "${item.nummer}" wirklich löschen?`)) return;
    
    try {
      await poolKleidungApi.delete(item.id);
      toast.success('Gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const filteredKleidung = kleidung.filter(item => {
    if (item.typ !== activeTab) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.nummer.toLowerCase().includes(search) ||
      item.groesse?.toLowerCase().includes(search) ||
      item.aktueller_nutzer_name?.toLowerCase().includes(search)
    );
  });

  const verfuegbar = filteredKleidung.filter(i => i.status === 'VERFUEGBAR');
  const ausgeliehen = filteredKleidung.filter(i => i.status === 'AUSGELIEHEN');

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Shirt className="w-8 h-8" />
              Pool-Kleidung
            </h1>
            <p className="text-muted-foreground mt-1">
              Digitaler Schrank für Leih-Einsatzkleidung
            </p>
          </div>
          {canManagePool && <Button onClick={() => { setSelectedKleidung(null); setShowCreateDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Hinzufügen
          </Button>}
        </div>

        {/* Statistik Cards */}
        {statistik && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(statistik).map(([typ, stats]) => (
              <Card key={typ}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-5">
                    {/* Icon links, deutlich größer, transparenter Hintergrund */}
                    <div className="shrink-0 flex items-center justify-center w-36 h-36">
                      <UniformIcon typ={typ} size={144} />
                    </div>
                    {/* Stats rechts daneben */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate mb-2">{typLabels[typ]}</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Gesamt:</span>
                          <span className="font-medium">{stats.gesamt}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-green-600">Verfügbar:</span>
                          <span className="font-medium text-green-600">{stats.verfuegbar}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-amber-600">Ausgeliehen:</span>
                          <span className="font-medium text-amber-600">{stats.ausgeliehen}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Suchen nach Nummer, Größe oder Nutzer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 h-auto p-1">
            {Object.entries(typLabels).map(([key, label]) => (
              <TabsTrigger key={key} value={key} className="flex items-center gap-3 py-2 text-base">
                <UniformIcon typ={key} size={40} />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.keys(typLabels).map(typ => (
            <TabsContent key={typ} value={typ} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Verfügbar */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Verfügbar ({verfuegbar.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {verfuegbar.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        Keine verfügbaren Kleidungsstücke
                      </p>
                    ) : (
                      verfuegbar.map(item => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent transition-colors"
                        >
                          <div>
                            <p className="font-medium">{item.nummer}</p>
                            {item.groesse && (
                              <p className="text-sm text-muted-foreground">Größe {item.groesse}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAusleihen(item)}
                            >
                              <ArrowRightLeft className="w-4 h-4 mr-1" />
                              Ausleihen
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleShowHistorie(item)}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(item)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>

                {/* Ausgeliehen */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-amber-600" />
                      Ausgeliehen ({ausgeliehen.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {ausgeliehen.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        Keine ausgeliehenen Kleidungsstücke
                      </p>
                    ) : (
                      ausgeliehen.map(item => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/20"
                        >
                          <div>
                            <p className="font-medium">{item.nummer}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {item.aktueller_nutzer_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleZurueckgeben(item)}
                            >
                              Zurückgeben
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleShowHistorie(item)}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Dialogs */}
      <CreateDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={loadData}
      />
      
      <AusleihenDialog
        open={showAusleihenDialog}
        onOpenChange={setShowAusleihenDialog}
        kleidung={selectedKleidung}
        mitarbeiter={mitarbeiter}
        currentUser={user}
        canManagePool={canManagePool}
        onSuccess={loadData}
      />

      <HistorieDialog
        open={showHistorieDialog}
        onOpenChange={setShowHistorieDialog}
        kleidung={selectedKleidung}
        historie={historie}
      />
    </MainLayout>
  );
}

// Create Dialog
function CreateDialog({ open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    typ: 'JACKE',
    nummer: '',
    groesse: '',
    notizen: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await poolKleidungApi.create(formData);
      toast.success('Kleidungsstück erstellt');
      onOpenChange(false);
      onSuccess();
      setFormData({ typ: 'JACKE', nummer: '', groesse: '', notizen: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neues Kleidungsstück</DialogTitle>
          <DialogDescription>
            Fügen Sie ein neues Kleidungsstück zum Pool hinzu
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Typ *</Label>
            <Select value={formData.typ} onValueChange={(v) => setFormData(p => ({ ...p, typ: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(typLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    <span className="inline-flex items-center gap-2">
                      <UniformIcon typ={key} size={18} />
                      {label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Nummer *</Label>
            <Input
              required
              value={formData.nummer}
              onChange={(e) => setFormData(p => ({ ...p, nummer: e.target.value }))}
              placeholder="z.B. J-013, H-005"
            />
          </div>

          <div className="space-y-2">
            <Label>Größe</Label>
            <Input
              value={formData.groesse}
              onChange={(e) => setFormData(p => ({ ...p, groesse: e.target.value }))}
              placeholder="z.B. M, L, 52"
            />
          </div>

          <div className="space-y-2">
            <Label>Notizen</Label>
            <Input
              value={formData.notizen}
              onChange={(e) => setFormData(p => ({ ...p, notizen: e.target.value }))}
              placeholder="Zusätzliche Infos..."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Wird erstellt...' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Rollen, die für andere Mitarbeiter ausleihen dürfen
const PRIVILEGED_ROLES = ['ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'];

// Ausleihen Dialog
function AusleihenDialog({ open, onOpenChange, kleidung, mitarbeiter, currentUser, canManagePool, onSuccess }) {
  const canChooseOther = canManagePool || PRIVILEGED_ROLES.includes(currentUser?.rolle);
  const [mitarbeiterId, setMitarbeiterId] = useState('');
  const [notiz, setNotiz] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Default: eigene User-ID
  useEffect(() => {
    if (open) {
      setMitarbeiterId(currentUser?.id || '');
      setNotiz('');
    }
  }, [open, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = { notiz: notiz || undefined };
      // Nur senden, wenn privilegiert und abweichend
      if (canChooseOther && mitarbeiterId && mitarbeiterId !== currentUser?.id) {
        payload.mitarbeiter_id = mitarbeiterId;
      }
      await poolKleidungApi.ausleihen(kleidung.id, payload);
      toast.success('Kleidung ausgeliehen');
      onOpenChange(false);
      onSuccess();
      setNotiz('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ausleihen');
    } finally {
      setIsLoading(false);
    }
  };

  if (!kleidung) return null;

  // Für die Anzeige: aktuell gewählter Mitarbeiter
  const selectedName = (() => {
    if (mitarbeiterId === currentUser?.id) {
      return `${currentUser?.vorname || ''} ${currentUser?.nachname || ''} (du)`.trim();
    }
    const m = mitarbeiter.find(x => x.id === mitarbeiterId);
    return m ? `${m.vorname} ${m.nachname}` : '';
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Kleidung ausleihen</DialogTitle>
          <DialogDescription>
            {typLabels[kleidung.typ]} {kleidung.nummer}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {canChooseOther ? (
            <div className="space-y-2">
              <Label>Mitarbeiter *</Label>
              <Select value={mitarbeiterId} onValueChange={setMitarbeiterId} required>
                <SelectTrigger data-testid="ausleihen-mitarbeiter-select">
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {currentUser?.id && (
                    <SelectItem value={currentUser.id}>
                      {currentUser.vorname} {currentUser.nachname} (du)
                    </SelectItem>
                  )}
                  {mitarbeiter
                    .filter(m => m.id !== currentUser?.id)
                    .map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.vorname} {m.nachname}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Als {currentUser?.rolle?.toLowerCase().replace(/_/g, ' ')} kannst du auch für andere ausleihen.
              </p>
            </div>
          ) : (
            <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="ausleihen-self-info">
              <div className="text-xs text-muted-foreground">Ausleihe für</div>
              <div className="font-medium">{selectedName || '–'}</div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Notiz (optional)</Label>
            <Input
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="z.B. Grund der Ausleihe..."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading || !mitarbeiterId} data-testid="ausleihen-submit-btn">
              {isLoading ? 'Wird ausgeliehen...' : 'Ausleihen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Historie Dialog
function HistorieDialog({ open, onOpenChange, kleidung, historie }) {
  if (!kleidung) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historie</DialogTitle>
          <DialogDescription>
            {typLabels[kleidung.typ]} {kleidung.nummer}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto">
          {historie.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Keine Historie vorhanden
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Mitarbeiter</TableHead>
                  <TableHead>Notiz</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historie.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm">
                      {new Date(entry.zeitstempel).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={entry.aktion === 'AUSGELIEHEN' ? 'default' : 'secondary'}>
                        {entry.aktion === 'AUSGELIEHEN' ? 'Ausgeliehen' : 'Zurückgegeben'}
                      </Badge>
                    </TableCell>
                    <TableCell>{entry.mitarbeiter_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {entry.notiz || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
